"use client";

import Link from "next/link";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { usePageTitle } from "@/lib/usePageTitle";
import {
  DownloadIcon,
  ServerIcon,
  LinkIcon,
  UsersIcon,
  OnlineIcon,
  ArrowRightIcon,
  BookIcon,
  GitHubIcon,
} from "@/components/icons";

const PLUGIN_VERSION = "1.0.1";
const PLUGIN_FILENAME = `BlockyNetwork-${PLUGIN_VERSION}.jar`;
const PLUGIN_REPO_URL = "https://github.com/Chewy42/blockynetwork-hytale-plugin";
const PLUGIN_DOWNLOAD_URL =
  "https://github.com/Chewy42/blockynetwork-hytale-plugin/releases/latest/download/BlockyNetwork-1.0.1.jar";

const features = [
  {
    icon: <LinkIcon className="h-6 w-6" />,
    title: "Player Account Linking",
    description: "Players can link their in-game accounts with a simple /link command",
  },
  {
    icon: <ServerIcon className="h-6 w-6" />,
    title: "Server Organization Linking",
    description: "Connect your Hytale server to your BlockyNetworks organization",
  },
  {
    icon: <OnlineIcon className="h-6 w-6" />,
    title: "Real-time Player Tracking",
    description: "Heartbeat system keeps player status synced between game and web",
  },
  {
    icon: <UsersIcon className="h-6 w-6" />,
    title: "Seamless Integration",
    description: "Web-to-game integration that just works - no complicated setup",
  },
];

const installSteps = [
  "Download the BlockyNetwork plugin JAR file below",
  "Place the JAR file in your Hytale server's mods/ folder",
  "Restart your server to generate the config file",
  "Configure blockynetwork.json with your organization details",
  "Use /link server in-game to connect to BlockyNetworks",
];

export default function DownloadsPage() {
  usePageTitle("Downloads");
  return (
    <div className="flex min-h-screen flex-col bg-background">
      <Navbar />

      <main className="flex-1">
        {/* Hero Section */}
        <section className="border-b border-border bg-gradient-to-b from-primary/5 to-background py-16">
          <div className="mx-auto max-w-4xl px-4 text-center sm:px-6 lg:px-8">
            <Badge className="mb-4 bg-primary/10 text-primary hover:bg-primary/10">
              v{PLUGIN_VERSION} Release
            </Badge>
            <h1 className="mb-4 text-4xl font-bold tracking-tight sm:text-5xl">
              BlockyNetwork Plugin
            </h1>
            <p className="mx-auto mb-8 max-w-2xl text-lg text-muted">
              The official companion plugin for Hytale servers. Connect your server
              to BlockyNetworks and unlock real-time player tracking, account
              linking, and seamless web integration.
            </p>
            <div className="flex flex-col items-center justify-center gap-4 sm:flex-row">
              <a href={PLUGIN_DOWNLOAD_URL} target="_blank" rel="noopener noreferrer">
                <Button size="lg" className="gap-2">
                  <DownloadIcon className="h-5 w-5" />
                  {`Download ${PLUGIN_FILENAME}`}
                </Button>
              </a>
              <Link href="/documentation">
                <Button size="lg" variant="outline" className="gap-2">
                  <BookIcon className="h-5 w-5" />
                  View Documentation
                </Button>
              </Link>
            </div>
            <div className="mt-4 flex justify-center">
              <a
                href={PLUGIN_REPO_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="group inline-flex h-10 w-10 items-center justify-center rounded-full border border-border text-muted transition-all duration-200 hover:-translate-y-0.5 hover:border-primary/50 hover:text-primary"
                aria-label="View BlockyNetwork plugin repository on GitHub"
              >
                <GitHubIcon className="h-5 w-5 transition-transform duration-200 group-hover:scale-110" />
              </a>
            </div>
            <p className="mt-4 text-sm text-muted">
              {PLUGIN_FILENAME} - Requires Hytale Server
            </p>
          </div>
        </section>

        {/* Features Section */}
        <section className="py-16">
          <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
            <h2 className="mb-12 text-center text-3xl font-bold">
              Plugin Features
            </h2>
            <div className="grid gap-6 sm:grid-cols-2">
              {features.map((feature, index) => (
                <Card key={index} className="transition-colors hover:bg-foreground/[0.02]">
                  <CardHeader className="flex flex-row items-center gap-4 pb-2">
                    <div className="rounded-lg bg-primary/10 p-3 text-primary">
                      {feature.icon}
                    </div>
                    <CardTitle className="text-lg">{feature.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted">{feature.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Installation Section */}
        <section className="border-t border-border bg-foreground/5 py-16">
          <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
            <h2 className="mb-8 text-center text-3xl font-bold">
              Quick Installation
            </h2>
            <Card>
              <CardContent className="pt-6">
                <ol className="space-y-4">
                  {installSteps.map((step, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary text-sm font-medium text-primary-foreground">
                        {index + 1}
                      </span>
                      <span className="text-foreground">{step}</span>
                    </li>
                  ))}
                </ol>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* System Requirements */}
        <section className="py-16">
          <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
            <h2 className="mb-8 text-center text-3xl font-bold">
              System Requirements
            </h2>
            <div className="grid gap-4 sm:grid-cols-3">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">Server</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted">Hytale Server (any version)</p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">Java</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted">Java 17 or higher</p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">Network</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted">Internet connection required</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="border-t border-border bg-foreground/5 py-16">
          <div className="mx-auto max-w-2xl px-4 text-center sm:px-6 lg:px-8">
            <h2 className="mb-4 text-3xl font-bold">Ready to get started?</h2>
            <p className="mb-8 text-muted">
              Download the plugin, set up your server, and join the BlockyNetworks
              community today.
            </p>
            <div className="flex flex-col items-center justify-center gap-4 sm:flex-row">
              <a href={PLUGIN_DOWNLOAD_URL} target="_blank" rel="noopener noreferrer">
                <Button size="lg" className="gap-2">
                  <DownloadIcon className="h-5 w-5" />
                  {`Download ${PLUGIN_FILENAME}`}
                </Button>
              </a>
              <Link href="/documentation">
                <Button size="lg" variant="outline" className="gap-2">
                  Read the Docs
                  <ArrowRightIcon className="h-5 w-5" />
                </Button>
              </Link>
            </div>
            <div className="mt-4 flex justify-center">
              <a
                href={PLUGIN_REPO_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="group inline-flex h-10 w-10 items-center justify-center rounded-full border border-border text-muted transition-all duration-200 hover:-translate-y-0.5 hover:border-primary/50 hover:text-primary"
                aria-label="View BlockyNetwork plugin repository on GitHub"
              >
                <GitHubIcon className="h-5 w-5 transition-transform duration-200 group-hover:scale-110" />
              </a>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
